import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  checkHealth,
  listClientDeviceLogs,
  listClientDevices,
  listClientLogs,
  listClients,
  listDevices,
  provisionDevice as apiProvisionDevice,
  registerClient,
  revokeDevice as apiRevokeDevice,
  rotateDeviceKey as apiRotateDeviceKey,
} from '../lib/api/client';
import {
  ApiAuditLog,
  ApiClient,
  ApiDevice,
  ProvisionDeviceRequest,
  ProvisionDeviceResponse,
  RotateDeviceKeyRequest,
} from '../lib/api/contracts';
import { useAuth } from './AuthContext';

type LogsByClient = Record<string, ApiAuditLog[]>;
type LogsByDevice = Record<string, ApiAuditLog[]>;

interface AdminDataContextValue {
  loading: boolean;
  error: string | null;
  health: string | null;
  clients: ApiClient[];
  devices: ApiDevice[];
  logsByClient: LogsByClient;
  logsByDevice: LogsByDevice;
  refreshAll: () => Promise<void>;
  createClient: (name: string) => Promise<ApiClient>;
  provisionDevice: (payload: ProvisionDeviceRequest) => Promise<ProvisionDeviceResponse>;
  revokeDevice: (deviceId: string) => Promise<void>;
  rotateDeviceKey: (payload: RotateDeviceKeyRequest) => Promise<void>;
  fetchClientDevices: (clientId: string) => Promise<ApiDevice[]>;
  fetchClientLogs: (clientId: string, limit?: number, offset?: number) => Promise<ApiAuditLog[]>;
  fetchClientDeviceLogs: (
    clientId: string,
    deviceId: string,
    limit?: number,
    offset?: number
  ) => Promise<ApiAuditLog[]>;
}

const AdminDataContext = createContext<AdminDataContextValue | undefined>(undefined);

export const AdminDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<string | null>(null);
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [devices, setDevices] = useState<ApiDevice[]>([]);
  const [logsByClient, setLogsByClient] = useState<LogsByClient>({});
  const [logsByDevice, setLogsByDevice] = useState<LogsByDevice>({});

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [healthText, clientRows, deviceRows] = await Promise.all([
        checkHealth().catch(() => 'Unavailable'),
        listClients(),
        listDevices(),
      ]);

      setHealth(healthText);
      setClients(clientRows);
      setDevices(deviceRows);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load admin data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setLoading(false);
      setError(null);
      setHealth(null);
      setClients([]);
      setDevices([]);
      return;
    }

    refreshAll();
  }, [authLoading, refreshAll, user]);

  const createClient = useCallback(
    async (name: string) => {
      const client = await registerClient({ name });
      await refreshAll();
      return client;
    },
    [refreshAll]
  );

  const revokeDevice = useCallback(
    async (deviceId: string) => {
      await apiRevokeDevice({ device_id: deviceId });
      await refreshAll();
    },
    [refreshAll]
  );

  const provisionDevice = useCallback(
    async (payload: ProvisionDeviceRequest) => {
      const device = await apiProvisionDevice(payload);
      await refreshAll();
      return device;
    },
    [refreshAll]
  );

  const rotateDeviceKey = useCallback(
    async (payload: RotateDeviceKeyRequest) => {
      await apiRotateDeviceKey(payload);
      await refreshAll();
    },
    [refreshAll]
  );

  const fetchClientDevices = useCallback(async (clientId: string) => {
    return listClientDevices({ client_id: clientId });
  }, []);

  const fetchClientLogs = useCallback(async (clientId: string, limit = 25, offset = 0) => {
    const logs = await listClientLogs({
      client_id: clientId,
      limit,
      offset,
    });

    setLogsByClient((current) => ({
      ...current,
      [clientId]: logs,
    }));

    return logs;
  }, []);

  const fetchClientDeviceLogs = useCallback(async (_clientId: string, deviceId: string, limit = 25, offset = 0) => {
    const logs = await listClientDeviceLogs({
      device_id: deviceId,
      limit,
      offset,
    });

    setLogsByDevice((current) => ({
      ...current,
      [deviceId]: logs,
    }));

    return logs;
  }, []);

  const value = useMemo<AdminDataContextValue>(
    () => ({
      loading,
      error,
      health,
      clients,
      devices,
      logsByClient,
      logsByDevice,
      refreshAll,
      createClient,
      provisionDevice,
      revokeDevice,
      rotateDeviceKey,
      fetchClientDevices,
      fetchClientLogs,
      fetchClientDeviceLogs,
    }),
    [
      clients,
      devices,
      error,
      createClient,
      provisionDevice,
      revokeDevice,
      rotateDeviceKey,
      fetchClientDeviceLogs,
      fetchClientDevices,
      fetchClientLogs,
      health,
      loading,
      logsByClient,
      logsByDevice,
      refreshAll,
    ]
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
};

export const useAdminData = (): AdminDataContextValue => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within an AdminDataProvider');
  }
  return context;
};
